import React from "react";
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useFormik, FormikProvider } from "formik";
import { useGetEmployeeProfileQuery, useUpdateEmployeeProfileMutation } from "../../../api/employeesApi";
import { useFormSubmission } from "../../../hooks/useFormSubmission";
import { useAppSelector } from "../../../app/hooks";
import { 
  employeeProfileValidationSchema, 
  EmployeeProfileFormValues,
  getInitialEmployeeProfileValues 
} from "../validation/employeeProfileValidation";
import EmployeeProfileFormContent from "./EmployeeProfileFormContent";
import { AddButton } from "../../../components/common/AddButton";

const EmployeeProfileTab: React.FC = () => {
  const authUser = useAppSelector((state) => state.auth.user);
  const [updateProfile] = useUpdateEmployeeProfileMutation();
  
  // Fetch employee profile information
  const { 
    data: employeeProfile, 
    isLoading: profileLoading, 
    error: profileError 
  } = useGetEmployeeProfileQuery(authUser?.id!, {
    skip: !authUser?.id,
  });

  const {
    isSubmitting,
    submitError,
    successMessage,
    handleSubmit,
    clearMessages,
  } = useFormSubmission({
    onSuccess: () => {
      // Success message will be shown in the form
    },
  });

  const formik = useFormik<EmployeeProfileFormValues>({
    initialValues: getInitialEmployeeProfileValues(employeeProfile),
    validationSchema: employeeProfileValidationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (!authUser?.id) {
        return;
      }

      // Use the user ID (backend will find the employee record by userId)
      await handleSubmit(async () => {
        await updateProfile({
          userId: authUser.id,
          profile: {
            dateOfBirth: values.dateOfBirth || undefined,
            familyHoliday: values.familyHoliday || undefined,
            street: values.street || undefined,
            city: values.city || undefined,
            state: values.state || undefined,
            zipCode: values.zipCode || undefined,
            country: values.country || undefined,
            phoneNumber: values.phoneNumber || undefined,
            emergencyContactName: values.emergencyContactName || undefined,
            emergencyContactPhone: values.emergencyContactPhone || undefined,
          },
        }).unwrap();
      });
    },
  });

  // Loading state
  if (profileLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (profileError) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Alert severity="error">
          Failed to load employee profile. Please try again.
        </Alert>
      </Box>
    );
  }

  // If no profile data is available, show a message
  if (!employeeProfile && !profileLoading) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Employee Profile
        </Typography>
        <Typography variant="body1" color="text.secondary">
          This section is only available for employees. Your account is not associated with an employee record.
        </Typography>
      </Box>
    );
  }

  return (
    <FormikProvider value={formik}>
      <Box>
        <Typography variant="h6" gutterBottom>
          Employee Profile Information
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Update your personal information and emergency contact details.
        </Typography>

        {/* Error and Success Messages */}
        {submitError && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={clearMessages}>
            {submitError}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={clearMessages}>
            {successMessage}
          </Alert>
        )}

        {/* Form Content */}
        <Box component="form" onSubmit={formik.handleSubmit}>
          <EmployeeProfileFormContent />
          
          {/* Submit Button */}
          <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end" }}>
            <AddButton
              type="submit"
              showIcon={false}
              disabled={isSubmitting || !formik.isValid}
              sx={{
                minWidth: '120px',
                backgroundColor: isSubmitting || !formik.isValid ? '#ccc' : undefined,
                '&:hover': {
                  backgroundColor: isSubmitting || !formik.isValid ? '#ccc' : undefined,
                },
              }}
            >
              {isSubmitting ? "Updating..." : "Update Profile"}
            </AddButton>
          </Box>
        </Box>
      </Box>
    </FormikProvider>
  );
};

export default EmployeeProfileTab;
